import { usePackageGrantsQuery } from '../../lib/Queries';
import Loading from '../Loading';
import EditGrantPanel from './EditGrantPanel.jsx';
import React, {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline/index.js';
import {
    truncate,
    truncateWords,
    ucfirst,
    uniqueArray,
} from '../../lib/Utils.js';
import {
    humanGrantRedeemableAmount,
    humanRedeemableAmount,
    PackageTypes,
} from '../../lib/PackageHelpers.js';
import { formatDateShort } from '../../lib/DateTime.js';
import Select from '~/components/Select';

export const GrantColumnHeadings = ({
                                        showPackageName,
                                        nameHeading,
                                        isRenewing,
                                    }) => {
    return (
        <div className="hidden md:flex items-center justify-between mx-6">
            <div className="flex-1 column-label">Client</div>
            {!!showPackageName && (
                <div className="flex-1 column-label">
                    {nameHeading || 'Package'}
                </div>
            )}
            <div className="flex-1 column-label">Status</div>
            <div className="flex-1 column-label">Remaining</div>
            <div className="flex-1 column-label">
                {isRenewing ? 'Renews' : 'Expires'}
            </div>
            <div className="w-6"></div>
        </div>
    );
};

export const GrantListRow = ({ grant, showPackageName, isRenewing, nameHeading }) => {
    return (
        <>
            {/* Desktop view */}
            <div className="hidden md:flex items-center justify-between">
                <div className="flex-1">
                    <div>
                        {truncateWords(grant.client?.name || 'No client', 15)}
                    </div>
                    <div className="text-gray-500 text-sm">
                        {truncate(grant.client?.email, 15)}
                    </div>
                </div>
                {!!showPackageName && (
                    <div className="flex-1">
                        {grant.package?.name || 'Unknown Package'}
                    </div>
                )}
                <div className="flex-1"><StatusBadge grant={grant} /></div>
                <div className="flex-1">{humanGrantRedeemableAmount(grant)}</div>
                <div className="flex-1">
                    {grant?.expires_at
                        ? formatDateShort(grant?.expires_at)
                        : 'No expiration'}
                    {isRenewing && !grant?.next_renewal_at && (
                        <div className="text-sm text-gray-500">Expiring</div>
                    )}
                </div>
            </div>

            {/* Mobile view */}
            <div className="md:hidden space-y-2">
                <div className="flex justify-between items-center gap-4 pr-4">
                    <span className="column-label text-sm font-medium flex-shrink-0">Client</span>
                    <div className="text-right flex-1">
                        <div>{grant.client?.name || 'No client'}</div>
                    </div>
                </div>
                <div className="flex justify-between items-center gap-4 pr-4 pt-2 border-t">
                    <span className="column-label text-sm font-medium flex-shrink-0">Email</span>
                    <div className="text-right flex-1">
                        {grant.client?.email}
                    </div>
                </div>
                {!!showPackageName && (
                    <div className="flex justify-between items-center gap-4 pr-4 pt-2 border-t">
                        <span className="column-label text-sm font-medium flex-shrink-0">
                            {nameHeading || 'Package'}
                        </span>
                        <span className="text-right flex-1">
                            {grant.package?.name || 'Unknown Package'}
                        </span>
                    </div>
                )}
                <div className="flex justify-between items-center gap-4 pt-2 pr-4 border-t">
                    <span className="column-label text-sm font-medium flex-shrink-0">Status</span>
                    <span className="text-right flex-1"><StatusBadge grant={grant} className="justify-end" /></span>
                </div>
                <div className="flex justify-between items-center gap-4 pt-2 pr-4 border-t">
                    <span className="column-label text-sm font-medium flex-shrink-0">Remaining</span>
                    <span className="text-right flex-1">{humanGrantRedeemableAmount(grant)}</span>
                </div>
                <div className="flex justify-between items-center gap-4 pt-2 pr-4 border-t">
                    <span className="column-label text-sm font-medium flex-shrink-0">
                        {isRenewing ? 'Renews' : 'Expires'}
                    </span>
                    <div className="text-right flex-1">
                        {grant?.expires_at
                            ? formatDateShort(grant?.expires_at)
                            : 'No expiration'}
                        {isRenewing && !grant?.next_renewal_at && (
                            <div className="text-sm text-gray-500">Expiring</div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export const StatusBadge = ({ grant, className="" }) => {
    const status = grant.status || 'active';

    let color = 'bg-red-100 !text-red-700';
    if (status === 'active') {
        color = 'bg-green-100 !text-green-700';
    }
    if (status === 'ending' || status === 'canceled') {
        color = 'bg-yellow-100 !text-yellow-700';
    }

    return (
        <div className={`flex ${className}`}>
            <div className={`tag ${color}`}>{ucfirst(status)}</div>
        </div>
    );
};

const GrantRow = ({
                      grant,
                      isFirst,
                      onClick,
                      content,
                      refetch,
                      manualOnClick,
                  }) => {
    return (
        <div
            className={`${!isFirst ? 'md:border-t' : ''} bg-white rounded-lg md:rounded-none flex pl-6 py-3 items-center justify-between md:pr-4 md:first:rounded-t-lg md:last:rounded-b-lg`}
        >
            <div
                className="flex-1 cursor-pointer"
                onClick={() => !manualOnClick && onClick()}
            >
                {typeof content === 'function'
                    ? content({
                        grant,
                        statusBadge: <StatusBadge grant={grant} />,
                        refetch,
                        onClick,
                    })
                    : 'Error, not  function'}
            </div>
            {!manualOnClick && (
                <div onClick={onClick} className="hidden md:block">
                    <ChevronRightIcon className="w-5 h-5 text-gray-300" />
                </div>
            )}
        </div>
    );
};

const GrantFilters = ({ filters, setFilters, plural, grants, type }) => {
    // Canceled/ending are only ever set on subscription grants
    const statusOptions = useMemo(() => {
        const options = {
            '': 'All Statuses',
            active: 'Active',
            expired: 'Expired',
            empty: 'Empty',
        };

        const hasSubscriptions =
            type === PackageTypes.SUBSCRIPTION ||
            grants.some(
                grant => grant.package?.type === PackageTypes.SUBSCRIPTION,
            );

        if (hasSubscriptions) {
            options.canceled = 'Canceled';
            options.ending = 'Ending';
        }

        return options;
    }, [type, grants]);

    const packageOptions = useMemo(() => {
        const uniqueGrants = grants.filter((grant, idx) => {
            return (
                grants.findIndex(g => g.package_id === grant.package_id) === idx
            );
        });

        if (uniqueGrants.length <= 1) {
            return null;
        }

        return [
            { value: '', label: `All ${ucfirst(plural) || 'packages'}` },
            ...uniqueGrants.map(grant => ({
                value: grant.package_id,
                label: grant.package?.name,
            })),
        ];
    }, [grants]);

    return (
        <div className="flex items-center sm:justify-end mb-4">
            {!!packageOptions && (
                <Select
                    options={packageOptions}
                    value={filters?.package}
                    onChange={value =>
                        setFilters(old => ({ ...old, package: value }))
                    }
                    className="mr-4 max-w-64"
                />
            )}

            <Select
                options={statusOptions}
                className="w-full sm:w-auto"
                inputClassName="w-full sm:w-auto"
                value={filters?.status}
                onChange={value =>
                    setFilters(old => ({ ...old, status: value }))
                }
            />
        </div>
    );
};

const GrantList = forwardRef(
    (
        {
            search,
            hideWhenEmpty,
            rowContent,
            headerContent,
            footerContent,
            manualOnClick,
            plural,
            showFilters,
        },
        ref,
    ) => {
        const { data, isLoading, refetch } = usePackageGrantsQuery(search);
        let grants = data || [];
        const [showNewGrantPanel, setShowNewGrantPanel] = useState(false);
        const [editGrant, setEditGrant] = useState('0');
        const [filters, setFilters] = useState({});

        function showNewGrant() {
            setEditGrant(null);
            setShowNewGrantPanel(true);
        }

        useImperativeHandle(ref, () => ({
            showNewGrant,
            refetch,
        }));

        if (!grants.length && hideWhenEmpty) {
            return null;
        }

        function applyFilters(grant) {
            let statusMatches = true;
            if (filters?.status) {
                statusMatches = grant.status === filters.status;
            }

            let packageMatches = true;
            if (filters?.package) {
                packageMatches =
                    parseInt(grant.package_id) === parseInt(filters.package);
            }

            return statusMatches && packageMatches;
        }

        const filteredGrants = grants.filter(applyFilters);

        return (
            <div>
                {!!showFilters && grants.length > 0 && (
                    <GrantFilters
                        filters={filters}
                        setFilters={setFilters}
                        plural={plural}
                        grants={grants}
                        type={search?.type}
                    />
                )}
                {filteredGrants.length > 0 && headerContent({
                    grants,
                    showNewGrant: showNewGrant,
                })}
                <div className="space-y-3 md:space-y-0 !max-w-none">
                    {isLoading && <Loading className="p-6" />}
                    {!isLoading && filteredGrants.length === 0 && (
                        <div className="bg-white rounded p-4 pl-6 text-gray-500">
                            {!grants?.length && (
                                <>
                                    There are no clients with{' '}
                                    {plural || 'packages'}
                                </>
                            )}
                            {grants.length > 0 && (
                                <>
                                    There are no clients matching your filters
                                    with {plural || 'packages'}
                                </>
                            )}
                        </div>
                    )}
                    {typeof filteredGrants.map === 'function' &&
                        filteredGrants.map((grant, idx) => (
                            <GrantRow
                                grant={grant}
                                key={grant.id}
                                isFirst={idx === 0}
                                onClick={() => {
                                    setShowNewGrantPanel(true);
                                    setEditGrant(grant);
                                }}
                                refetch={refetch}
                                content={rowContent}
                                manualOnClick={manualOnClick}
                            />
                        ))}
                </div>
                {typeof footerContent === 'function' &&
                    footerContent({
                        grants,
                        showNewGrant: showNewGrant,
                    })}
                <EditGrantPanel
                    isOpen={showNewGrantPanel}
                    onClose={() => {
                        setShowNewGrantPanel(false);
                        setEditGrant(null);
                    }}
                    grant={editGrant}
                    packageId={search.packageId}
                    packageType={search.type}
                    refetch={refetch}
                />
            </div>
        );
    },
);

export default GrantList;
